import type React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { AuthButton } from '@/components/ui/AuthButton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

import { DraftMockup } from './DraftMockup';

export function Hero(): React.ReactElement {
  const navigate = useNavigate();

  const heroWords = [
    { text: "Publish", delay: 0 },
    { text: "authoritative", delay: 150, italic: true },
    { text: "articles", delay: 300 },
  ];

  const subWords = [
    { text: "without", delay: 450 },
    { text: "sacrificing", delay: 550 },
    { text: "nights", delay: 650 },
    { text: "&", delay: 720 },
    { text: "weekends", delay: 800 },
  ];

  return (
    <div className="relative pt-32 pb-24 md:pt-40 lg:pt-48 overflow-hidden bg-aged-cream text-charcoal">
      {/* Bold watercolor gradient background */}
      <div className="absolute inset-0 watercolor-gradient" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            {/* Decorative editorial elements */}
            <div className="flex items-center gap-3 mb-8 animate-fade-in">
              <div className="h-px w-10 bg-aged-brass" />
              <div className="w-2 h-2 rounded-full bg-bottle-green" />
            </div>

            <Badge variant="secondary" className="mb-8 animate-fade-in text-sm py-1.5 px-4 border-bottle-green/20 bg-champagne/10 text-bottle-green" style={{ animationDelay: '150ms' }}>
              Pay only for what you create
            </Badge>

            {/* Main headline with word-level animation */}
            <h1 className="font-display font-bold tracking-tight text-charcoal mb-8 leading-[1.05] text-[clamp(2.5rem,5vw+0.75rem,4.25rem)]">
              {heroWords.map((word) => (
                <span
                  key={word.text}
                  className={`inline-block animate-word-entrance ${word.italic ? 'italic text-bottle-green' : ''}`}
                  style={{ animationDelay: `${word.delay}ms`, marginRight: '0.3em' }}
                >
                  {word.text}
                </span>
              ))}
            </h1>

            {/* Subheadline with word-level animation */}
            <h2 className="font-display font-medium tracking-tight text-bottle-green mb-12 leading-[1.2] text-[clamp(1.5rem,3vw+0.5rem,2.25rem)]">
              {subWords.map((word) => (
                <span
                  key={word.text}
                  className="inline-block animate-word-entrance"
                  style={{ animationDelay: `${word.delay}ms`, marginRight: '0.25em' }}
                >
                  {word.text}
                </span>
              ))}
            </h2>

            <p className="font-body text-xl text-muted-warm mb-10 max-w-xl leading-relaxed animate-fade-in font-normal" style={{ animationDelay: '850ms' }}>
              AI-powered research and writing that sounds like you. Pay only for what you create, no subscription required.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center gap-4 mb-12 animate-fade-in" style={{ animationDelay: '950ms' }}>
              <AuthButton
                size="lg"
                className="group h-14 px-8 text-base rounded-lg bg-bottle-green text-white hover:bg-sage transition-colors border-bottle-green font-display"
                onAuthenticatedClick={() => navigate('/research/deep')}
              >
                Create Your First Article Free
                <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
              </AuthButton>

              <Button
                size="lg"
                variant="outline"
                className="h-14 px-8 text-base rounded-lg border-charcoal text-charcoal hover:bg-charcoal hover:text-white transition-colors font-body"
                onClick={() => {
                  document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                See How It Works
              </Button>
            </div>

            {/* Feature highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 animate-fade-in" style={{ animationDelay: '1100ms' }}>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-bottle-green/10 flex items-center justify-center shrink-0">
                  <div className="w-1.5 h-1.5 rounded-full bg-bottle-green" />
                </div>
                <span className="text-charcoal font-medium font-body text-sm">Automated Research</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-bottle-green/10 flex items-center justify-center shrink-0">
                  <div className="w-1.5 h-1.5 rounded-full bg-bottle-green" />
                </div>
                <span className="text-charcoal font-medium font-body text-sm">Authentic Voice</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-bottle-green/10 flex items-center justify-center shrink-0">
                  <div className="w-1.5 h-1.5 rounded-full bg-bottle-green" />
                </div>
                <span className="text-charcoal font-medium font-body text-sm">Full Editorial Control</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative hidden lg:block animate-fade-in" style={{ animationDelay: '400ms' }}>
            <DraftMockup />
          </div>
        </div>
      </div>
    </div>
  );
}
